import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

import { WaitSpinner } from "./WaitSpinner";

// Single upload control for the whole app (BAC-104), modelled on the PMS
// "explanatory video" uploader, which was the best of the three shapes that
// coexisted: this component's old two-step row, the video's label-button, and
// bespoke labels around raw inputs.
//
// Three states:
//   - empty      → just the trigger ("Upload file")
//   - uploading  → boxed spinner and a disabled trigger
//   - has a file → boxed name, optional preview and actions, and a trigger
//                  that reads "Replace"
//
// Picking a file uploads it straight away: the old "choose, then press
// Upload" second step is gone, so every screen behaves the same with one
// click less.
export function FileUploadComponent({
    onUpload = (file) => {},
    isUploading = false,
    fileType = 'image/jpeg,image/png',
    // Name (or description) of the file already stored on the server. Its
    // presence is what switches the control to the "has a file" state.
    fileName = null,
    // Optional: renders a remove link next to the name.
    onRemove = null,
    // Optional: anything to show inside the box under the name (an image, a
    // video player...).
    preview = null,
    // Optional: extra links next to remove, e.g. show/hide the preview.
    actions = null,
} = {}) {

    //LIBRARIES
    const { t } = useTranslation();

    // Screens with several uploads (logo and login background, say) share one
    // isUploading flag from their store, so uploading the logo would spin
    // both controls. Only the one that started the upload shows it.
    const [startedTheUpload, setStartedTheUpload] = useState(false);

    useEffect(() => {
        // The parents that report progress raise isUploading synchronously
        // inside onUpload (a plain store dispatch, batched with the pick's
        // own setState), so any render where the shared flag is down means
        // no upload of ours is in flight: clear the marker. This covers the
        // normal end of an upload (true→false edge) and also the marker
        // getting stuck when onUpload fails synchronously or the screen
        // never raises the flag — a stuck marker would make this control
        // spin along the next time a sibling sharing the store flag uploads.
        if (!isUploading && startedTheUpload) setStartedTheUpload(false);
    }, [isUploading, startedTheUpload]);

    const hasFile = !!fileName;
    const showUploading = isUploading && startedTheUpload;

    //VIEW
    if (showUploading) {
        return (
            <div className="border c-rounded-big p-3 bg-white">
                <div className="d-flex justify-content-center align-items-center py-4">
                    <WaitSpinner />
                </div>
                <div className="mt-2" />
                {trigger()}
            </div>
        );
    }

    if (!hasFile) return trigger();

    return (
        <div className="border c-rounded-big p-3 bg-white">
            <div className="d-flex align-items-center justify-content-between">
                <span className="text-truncate text-secondary">
                    {fileName}
                </span>
                <div className="d-flex align-items-center flex-shrink-0 ml-2">
                    {actions}
                    {onRemove && !isUploading &&
                        <span
                            className="cp text-danger c-font-weight-medium ml-3"
                            onClick={onRemove}
                        >
                            {t("global_remove")}
                        </span>
                    }
                </div>
            </div>
            {preview &&
                <>
                    <div className="mt-2" />
                    {preview}
                </>
            }
            <div className="mt-2" />
            {trigger()}
        </div>
    );

    function trigger() {
        return (
            <label className={`
                mb-0
                py-1
                px-3
                c-bd-primary
                rounded
                bg-white
                c-text-primary
                c-font-weight-medium
                ${isUploading ? "cna c-opacity-50" : "cp c-hover-bg-primary-light-2"}
            `}>
                {hasFile ? t("global_replace_file") : t("global_upload_file")}
                <input
                    type="file"
                    accept={fileType}
                    className="d-none"
                    disabled={isUploading}
                    onChange={handleSetFile}
                />
            </label>
        );
    }

    function handleSetFile(e) {
        const file = e.target.files[0];
        // Reset first so picking the same file again still fires onChange:
        // otherwise a failed upload could not be retried with that same file.
        e.target.value = "";

        if (!file) return;

        setStartedTheUpload(true);
        onUpload(file);
    }
}
